import { useMutation, useQuery } from "@tanstack/react-query";
import { getRouteApi } from "@tanstack/react-router";
import { useRef } from "react";
import { toast } from "sonner";
import { getApiErrorMessage } from "@/lib/api-error";
import { createSmartCreationPlan, fetchSmartCreationPlan } from "@/lib/smart-creation-api";
import {
  readSmartCreationAcceptedTask,
  smartCreationOwnerKey,
} from "@/lib/smart-creation-draft-store";
import { resolveSmartCreationShopId } from "@/lib/smart-creation-shop";
import { useAuthStore } from "@/stores/auth";

const campaignsRoute = getRouteApi("/_authed/campaigns/");

/**
 * Smart Creation entry state for the campaign list: which plan/task the URL
 * points at, which Shop that plan belongs to, and the create/switch-Shop
 * mutations that move the URL between plans.
 */
export function useCampaignSmartCreation(scopeShopIds: string[]) {
  const search = campaignsRoute.useSearch();
  const navigate = campaignsRoute.useNavigate();
  const currentShopId = useAuthStore((state) => state.currentShopId);
  const setCurrentShop = useAuthStore((state) => state.setCurrentShop);
  const planId = search.smartCreation;
  const selectedSmartCreationShopId = resolveSmartCreationShopId(scopeShopIds, currentShopId);
  const acceptedReceipt = planId ? readSmartCreationAcceptedTask(planId) : null;
  const acceptedTaskId = acceptedReceipt?.taskId;
  const creationTaskId = search.creationTask ?? acceptedTaskId;
  const creationIdentityRef = useRef<{ planId: string; shopId: string } | null>(null);
  const pendingCreationIdentityRef = useRef<{ planId: string; shopId: string } | null>(null);
  const smartCreationShopSwitchRef = useRef<string | null>(null);
  const identityQuery = useQuery({
    queryKey: ["smart-creation", "identity", smartCreationOwnerKey(), planId],
    queryFn: () => fetchSmartCreationPlan(planId!),
    enabled: Boolean(
      planId &&
        !creationTaskId &&
        creationIdentityRef.current?.planId !== planId &&
        pendingCreationIdentityRef.current?.planId !== planId,
    ),
    retry: false,
    staleTime: Infinity,
  });
  if (planId) {
    if (pendingCreationIdentityRef.current?.planId === planId) {
      creationIdentityRef.current = pendingCreationIdentityRef.current;
      pendingCreationIdentityRef.current = null;
    } else if (
      creationIdentityRef.current?.planId !== planId &&
      identityQuery.data?.id === planId
    ) {
      creationIdentityRef.current = {
        planId,
        shopId: identityQuery.data.shopId,
      };
    }
  } else if (!planId) {
    creationIdentityRef.current = null;
  }
  const shopId = planId
    ? creationIdentityRef.current?.planId === planId
      ? creationIdentityRef.current.shopId
      : null
    : selectedSmartCreationShopId;
  const open = Boolean(planId || creationTaskId);

  const close = () => {
    return navigate({
      search: (previous) => {
        const { smartCreation: _smartCreation, creationTask: _creationTask, ...rest } = previous;
        return rest;
      },
      replace: true,
    });
  };
  const createMutation = useMutation({
    mutationFn: () => {
      if (!shopId) throw new Error("Smart Creation requires a selected Shop");
      return createSmartCreationPlan(shopId);
    },
    onSuccess: (plan) => {
      pendingCreationIdentityRef.current = { planId: plan.id, shopId: plan.shopId };
      return navigate({
        search: (previous) => ({ ...previous, smartCreation: plan.id, creationTask: undefined }),
        replace: true,
      });
    },
    onError: async (error) => toast.error(await getApiErrorMessage(error)),
  });
  const switchShopMutation = useMutation({
    mutationFn: (nextShopId: string) => createSmartCreationPlan(nextShopId),
    onSuccess: async (nextPlan) => {
      // React Query can render once between mutation success and router navigation.
      // Keep using the valid old identity until the URL contains the new plan ID,
      // then adopt the new plan and shop together during render.
      pendingCreationIdentityRef.current = { planId: nextPlan.id, shopId: nextPlan.shopId };
      await navigate({
        search: (previous) => ({
          ...previous,
          smartCreation: nextPlan.id,
          creationTask: undefined,
        }),
        replace: true,
      });
      setCurrentShop(nextPlan.shopId);
    },
    onError: async (error) => toast.error(await getApiErrorMessage(error)),
  });
  const changeShop = async (nextShopId: string) => {
    if (nextShopId !== shopId && smartCreationShopSwitchRef.current == null) {
      smartCreationShopSwitchRef.current = nextShopId;
      try {
        await switchShopMutation.mutateAsync(nextShopId);
      } finally {
        smartCreationShopSwitchRef.current = null;
      }
    }
  };
  const acceptTask = (taskId: string) =>
    navigate({
      search: (previous) => ({
        ...previous,
        smartCreation: undefined,
        creationTask: taskId,
      }),
      replace: true,
    });
  const resumeDraft = (draftPlanId: string) =>
    navigate({
      search: (previous) => ({
        ...previous,
        creationTask: undefined,
        smartCreation: draftPlanId,
      }),
      replace: true,
    });

  return {
    planId,
    creationTaskId,
    acceptedTaskId,
    shopId,
    open,
    identityQuery,
    createMutation,
    switchShopMutation,
    close,
    changeShop,
    acceptTask,
    resumeDraft,
  };
}

export type CampaignSmartCreation = ReturnType<typeof useCampaignSmartCreation>;
